import React from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { Dispatch } from '@reduxjs/toolkit';
import {
  setSelectedSourceIdx,
  setSettings,
} from '../../../../support/slices/tileBuilderSlice';
import { TileSettings } from '../../../../types/tileBuilderTypes';
import { DataSource } from '../TilePreview/tilePreviewTypes';
import ChartAggregationControls from './ChartAggregationControls';
import { CHART_DATE_RANGE_NAMES } from './ChartControlProps';

interface DataFieldsControlsProps {
  chartId: string;
  settings: TileSettings;
  dispatch: Dispatch;
  selectedSourceIdx: number;
  allSources: DataSource[];
  isDatagrid: boolean;
  isMetric: boolean;
  isBarChart: boolean;
  isLineChart: boolean;
  derivedFields: string[];
  previewData?: any;
  dateFilterEnabled?: boolean;
  allowedDateRangeOptions: string[];
  visibleDateRangeOptions: { value: string; label: string }[];
  effectiveSelectedDateRange: string;
  currentTimeFilter?: string;
  handleOpenColumnsModal?: () => void;
  syncDateRange: (nextValue: string) => void;
}

const DataFieldsControls: React.FC<DataFieldsControlsProps> = ({
                                                                 chartId,
                                                                 settings,
                                                                 dispatch,
                                                                 selectedSourceIdx,
                                                                 allSources,
                                                                 isDatagrid,
                                                                 isMetric,
                                                                 isBarChart,
                                                                 isLineChart,
                                                                 derivedFields,
                                                                 previewData,
                                                                 dateFilterEnabled,
                                                                 allowedDateRangeOptions,
                                                                 visibleDateRangeOptions,
                                                                 effectiveSelectedDateRange,
                                                                 currentTimeFilter,
                                                                 handleOpenColumnsModal,
                                                                 syncDateRange,
                                                               }) => {
  return (
    <Box>
      <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
        <FormControl sx={{ minWidth: 320 }}>
          <InputLabel>Data Source</InputLabel>
          <Select
            value={selectedSourceIdx}
            label="Data Source"
            onChange={(e) =>
              dispatch(
                setSelectedSourceIdx({
                  chartId,
                  index: Number(e.target.value),
                })
              )
            }
          >
            {allSources.map((src, idx) => (
              <MenuItem key={idx} value={idx}>
                {src.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {isDatagrid && (
          <Button
            variant="contained"
            onClick={handleOpenColumnsModal}
          >
            Edit Columns
          </Button>
        )}
      </Box>

      {!Array.isArray(previewData) && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Preview data is not an array. Most chart types require array-shaped data after
          transformations.
        </Alert>
      )}

      {!isDatagrid && !isMetric && derivedFields.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />

          <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Label Field</InputLabel>
              <Select
                label="Label Field"
                value={settings.labelField ?? ''}
                onChange={(e) =>
                  dispatch(
                    setSettings({
                      chartId,
                      settings: { labelField: e.target.value as string },
                    })
                  )
                }
              >
                {derivedFields.map((f) => (
                  <MenuItem key={f} value={f}>
                    {f}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Custom Label for Label Field"
              value={settings.customLabel ?? ''}
              onChange={(e) =>
                dispatch(
                  setSettings({
                    chartId,
                    settings: { customLabel: e.target.value },
                  })
                )
              }
              sx={{ minWidth: 240 }}
            />

            <FormControl sx={{ minWidth: 220 }}>
              <InputLabel>Value Field</InputLabel>
              <Select
                label="Value Field"
                value={settings.valueField ?? 'Count'}
                onChange={(e) =>
                  dispatch(
                    setSettings({
                      chartId,
                      settings: { valueField: e.target.value as string },
                    })
                  )
                }
              >
                <ListSubheader>Aggregations</ListSubheader>
                <MenuItem value="Count">Count</MenuItem>
                <MenuItem value="Sum">Sum</MenuItem>
                <ListSubheader>Data Columns</ListSubheader>
                {derivedFields.map((f) => (
                  <MenuItem key={f} value={f}>
                    {f}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {(isBarChart || isLineChart) && (
            <>
              <Divider sx={{ my: 2 }} />
              <ChartAggregationControls
                chartId={chartId}
                settings={settings}
                dispatch={dispatch}
                chartKind={isBarChart ? 'bar' : 'line'}
                isLineChart={isLineChart}
                derivedFields={derivedFields}
              />
            </>
          )}

          <Divider sx={{ my: 2 }} />

          <Box className="flex flex-col gap-4">
            <FormControl disabled={!dateFilterEnabled}>
              <InputLabel>Date Range Options</InputLabel>
              <Select
                multiple
                label="Date Range Options"
                value={allowedDateRangeOptions}
                onChange={(e) => {
                  const nextOptions = [...(e.target.value as string[])];
                  const safeOptions =
                    nextOptions.length > 0
                      ? nextOptions
                      : [CHART_DATE_RANGE_NAMES[0].value];

                  dispatch(
                    setSettings({
                      chartId,
                      settings: {
                        dateRangeOptions: safeOptions,
                      },
                    })
                  );

                  const currentSelected =
                    currentTimeFilter ??
                    settings.defaultDateRange ??
                    CHART_DATE_RANGE_NAMES[0].value;

                  if (!safeOptions.includes(currentSelected)) {
                    const fallback = safeOptions[0] ?? CHART_DATE_RANGE_NAMES[0].value;
                    syncDateRange(fallback);
                  }
                }}
              >
                {CHART_DATE_RANGE_NAMES.map((scheme) => (
                  <MenuItem key={scheme.value} value={scheme.value}>
                    {scheme.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl disabled={!dateFilterEnabled}>
              <InputLabel>Default Date Range</InputLabel>
              <Select
                label="Default Date Range"
                value={effectiveSelectedDateRange}
                onChange={(e) => syncDateRange(e.target.value as string)}
              >
                {visibleDateRangeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </>
      )}
    </Box>
  );
};

export default DataFieldsControls;